/**
 * Build NumberLineModel plots.
 *
 * A simplified helper set for 6th-grade number-line work (integers, opposites,
 * simple inequalities). It depends only on Rational and the plot model - not on
 * any inequality solver - so conversion is a pure, deterministic, testable
 * function of its inputs.
 */

import type { Rational } from "../engine/rational";
import type { InequalityOp } from "../engine/answer";
import type { NumberLineModel, NumberLinePoint } from "./plot-model";

/** A sensible integer window around the given value(s), always including 0. */
export function windowFor(values: readonly Rational[]): {
  min: number;
  max: number;
} {
  const nums = values.map((v) => v.toNumber());
  const lo = Math.min(...nums, 0);
  const hi = Math.max(...nums, 0);
  const pad = 3;
  return { min: Math.floor(lo) - pad, max: Math.ceil(hi) + pad };
}

/** A number line marking one or more points (no shading). */
export function pointsNumberLine(
  values: readonly Rational[],
  opts: { closed?: boolean; win?: { min: number; max: number } } = {},
): NumberLineModel {
  const win = opts.win ?? windowFor(values);
  const closed = opts.closed ?? true;
  const points: NumberLinePoint[] = values.map((value) => ({ value, closed }));
  return {
    kind: "number-line",
    min: win.min,
    max: win.max,
    points,
    rays: [],
  };
}

/** A single-ray inequality (x op value) as a number-line plot. */
export function rayNumberLine(
  op: InequalityOp,
  value: Rational,
  win = windowFor([value]),
): NumberLineModel {
  const direction = op === "<" || op === "<=" ? "left" : "right";
  const closed = op === "<=" || op === ">=";
  return {
    kind: "number-line",
    min: win.min,
    max: win.max,
    points: [],
    rays: [{ from: value, direction, closed }],
  };
}
